if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');

      // Single shared iframe for all 3D viewers - avoids WebGL context creation/destruction crashes on iOS
      this.shared3DIframe = null;

      // Load the first active 3D viewer if present
      if (this.elements.viewer) {
        const activeSlide = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (activeSlide) this.load3DViewer(activeSlide);

        // Listen for slide changes to handle 3D viewer loading on swipe/navigation
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      }

      if (!this.elements.thumbnails) return;

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      if (thumbnail) this.setActiveThumbnail(thumbnail);

      // Load 3D viewer when slide changes (needed for mobile swipe navigation)
      const currentSlide = event.detail.currentElement;
      if (currentSlide) {
        this.load3DViewer(currentSlide);
      }
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      const is3DViewer = activeItem.querySelector('.product-3d-viewer');

      // Handle 3D viewer announcement
      if (is3DViewer) {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
        return;
      }

      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);

      // Handle 3D viewer loading
      this.load3DViewer(activeItem);
    }

    load3DViewer(item) {
      const viewer = item.querySelector('.product-3d-viewer');
      if (!viewer) return;

      const viewerUrl = viewer.dataset.viewerUrl;
      if (!viewerUrl) return;

      // Already showing this viewer - skip
      if (viewer.classList.contains('product-3d-viewer--loaded')) return;

      // Create shared iframe once (never destroyed - avoids iOS WebGL crashes)
      if (!this.shared3DIframe) {
        this.shared3DIframe = document.createElement('iframe');
        this.shared3DIframe.className = 'product-3d-viewer__iframe';
        this.shared3DIframe.setAttribute('frameborder', '0');
        this.shared3DIframe.setAttribute('allowfullscreen', '');
        this.shared3DIframe.setAttribute('allow', 'autoplay; fullscreen; xr-spatial-tracking');
        this.shared3DIframe.setAttribute('scrolling', 'no');
      }

      // Mark all viewers as not loaded
      this.elements.viewer.querySelectorAll('.product-3d-viewer').forEach(v => {
        v.classList.remove('product-3d-viewer--loaded');
      });

      // Move iframe to this viewer and set src
      viewer.appendChild(this.shared3DIframe);
      this.shared3DIframe.src = viewerUrl;
      viewer.classList.add('product-3d-viewer--loaded');
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
